<template>
  <layout>
    <div>
      <b-container @click="maintainFocus" class="attendance-container">
        <!-- Card 1 (initially hidden) -->
        <b-card
          ref="card1"
          class="card1"
          v-if="
            showCard1 && !showPaymentOverduePopup && this.member._id !== null
          "
        >
          <div class="card1-content">
            <div
              v-if="this.member.first_name != undefined"
              class="greeting-section"
            >
              <h1 class="greeting-title">Hi {{ this.member.first_name }},</h1>
              <h2 class="greeting-subtitle">{{ this.headerMsg }}</h2>
            </div>
            <div v-else class="greeting-section">
              <h2 class="greeting-title">{{ this.headerMsg }}</h2>
            </div>

            <div
              v-if="this.member.first_name != undefined"
              class="info-section"
            >
              <div class="info-card">
                <div class="info-label">Next Payment Date</div>
                <div class="info-value">
                  {{ this.member.next_payment_date }}
                </div>
              </div>
            </div>

            <div
              v-if="this.member.first_name != undefined"
              class="status-section"
            >
              <div class="status-badge">
                <i class="fa fa-check-circle"></i>
                {{ this.statusMsg }}
              </div>
            </div>
          </div>
        </b-card>

        <!-- Card 2 (initially visible) -->
        <b-card ref="card2" class="card2">
          <div class="scan-container">
            <div class="scan-icon-wrapper">
              <i class="fa fa-qrcode scan-icon"></i>
            </div>
            <h2 class="scan-title">Please scan your card / QR code</h2>
            <div class="input-wrapper">
              <b-form-input
                ref="barcodetextfield"
                type="number"
                :disabled="isLoading"
                v-model="barcode"
                @keyup="markAttendanceEvent"
                class="barcode-input"
                placeholder="Scan or enter barcode"
              ></b-form-input>
              <div v-if="isLoading" class="loading-indicator">
                <div class="spinner-small"></div>
              </div>
            </div>
          </div>
        </b-card>
      </b-container>

      <!-- Payment Overdue Popup -->
      <div v-if="showPaymentOverduePopup" class="payment-overdue-overlay">
        <div class="payment-overdue-popup">
          <div class="fail-icon-wrapper">
            <i class="fa fa-times-circle fail-icon"></i>
          </div>
          <h1 class="fail-title">Payment Overdue!</h1>
          <h2 class="fail-subtitle">Hi {{ this.member.first_name }},</h2>
          <div class="fail-message">
            <p>Your membership payment is past due.</p>
            <p class="fail-date">
              Due Date: {{ this.member.next_payment_date }}
            </p>
          </div>
          <div class="fail-action">
            <p>Please visit the reception to renew your membership.</p>
          </div>
          <div class="auto-close-timer">
            <p>This message will close in {{ popupCountdown }} seconds</p>
          </div>
        </div>
      </div>
    </div>
  </layout>
</template>

<script>
import Layout from '@layouts/main'
import uuidv4 from 'uuid/v4'

import {
  memberMethods,
  paymentMethods,
  attendanceMethods,
} from '@state/helpers'

export default {
  name: 'Attendance',
  components: {
    Layout,
  },
  data() {
    return {
      showCard1: false,
      showCard2: true,
      barcode: '',
      isLoading: false,
      member: {},
      headerMsg: 'Welcome',
      prevBarcode: '',
      statusMsg: '',
      isDayPassed: false,
      showPaymentOverduePopup: false,
      popupCountdown: 5,
      popupTimer: null,
    }
  },
  mounted() {
    this.$refs.barcodetextfield.focus()
    // Add global click handler to always maintain focus
    document.addEventListener('click', this.maintainFocus)
  },
  beforeDestroy() {
    // Clean up the event listener
    document.removeEventListener('click', this.maintainFocus)
  },
  methods: {
    maintainFocus() {
      this.$nextTick(() => {
        if (this.$refs.barcodetextfield) {
          this.$refs.barcodetextfield.focus()
        }
      })
    },
    playAlertSound() {
      try {
        // Create audio context
        const audioContext = new (window.AudioContext ||
          window.webkitAudioContext)()

        // Create oscillator for siren sound
        const oscillator = audioContext.createOscillator()
        const gainNode = audioContext.createGain()

        oscillator.connect(gainNode)
        gainNode.connect(audioContext.destination)

        // Siren sound - alternating frequencies
        oscillator.type = 'sine'

        // Set constant volume
        gainNode.gain.setValueAtTime(0.4, audioContext.currentTime)

        // Create siren effect by modulating frequency
        const now = audioContext.currentTime
        const duration = 2.5 // Total duration of siren
        const cycleTime = 0.5 // Time for one complete cycle (up and down)

        // Siren frequency range (800Hz to 1200Hz)
        const lowFreq = 700
        const highFreq = 1200

        // Create repeating siren pattern
        for (let i = 0; i < duration / cycleTime; i++) {
          const startTime = now + i * cycleTime
          const midTime = startTime + cycleTime / 2
          const endTime = startTime + cycleTime

          // Ramp up
          oscillator.frequency.setValueAtTime(lowFreq, startTime)
          oscillator.frequency.linearRampToValueAtTime(highFreq, midTime)

          // Ramp down
          oscillator.frequency.linearRampToValueAtTime(lowFreq, endTime)
        }

        // Fade out at the end
        gainNode.gain.setValueAtTime(0.4, now + duration - 0.2)
        gainNode.gain.linearRampToValueAtTime(0, now + duration)

        // Play the siren
        oscillator.start(now)
        oscillator.stop(now + duration)
      } catch (error) {
        console.error('Error playing alert sound:', error)
      }
    },
    ...memberMethods,
    ...paymentMethods,
    ...attendanceMethods,
    async swapCards() {
      // Perform the animation to move card2 down
      this.$refs.card2.classList.add('move-slightly-down')
      setTimeout(() => {
        // Show Card 1 after animation
        this.showCard1 = true
        this.barcode = ''

        // Scroll to top when Card 1 shows - scroll the main app container
        // Scroll to 0 or slightly below to ensure card is fully visible
        window.scrollTo({ top: 0, behavior: 'smooth' })
        // Also scroll the main content area if it exists
        const mainContainer = document.querySelector('.app-body .main')
        if (mainContainer) {
          mainContainer.scrollTo({ top: 0, behavior: 'smooth' })
        }
        // Wait a bit and scroll the page container to ensure card1 is visible
        setTimeout(() => {
          document.documentElement.scrollTop = 0
          document.body.scrollTop = 0
        }, 50)

        // Show popup if payment is overdue
        if (this.isDayPassed) {
          this.showPaymentOverduePopup = true
          this.popupCountdown = 5
          this.startPopupCountdown()
          this.playAlertSound()
        }

        this.$nextTick(() => {
          this.$refs.barcodetextfield.focus()
        })
        // After 10 seconds, hide Card 1 and move Card 2 back up
        setTimeout(() => {
          this.member = {}
          this.barcode = ''
          this.showCard1 = false
          this.$refs.card2.classList.remove('move-slightly-down')
          this.$nextTick(() => {
            this.$refs.barcodetextfield.focus()
          })
        }, 4000)
      }, 100) // Duration of the animation
    },
    startPopupCountdown() {
      if (this.popupTimer) {
        clearInterval(this.popupTimer)
      }

      this.popupTimer = setInterval(() => {
        this.popupCountdown--
        if (this.popupCountdown <= 0) {
          this.closePaymentPopup()
        }
      }, 1000)
    },
    closePaymentPopup() {
      if (this.popupTimer) {
        clearInterval(this.popupTimer)
        this.popupTimer = null
      }
      this.showPaymentOverduePopup = false
      this.popupCountdown = 5
    },
    async markAttendanceEvent() {
      if (this.barcode.length == 4) {
        debugger
        this.isLoading = true
        console.log('triggered')
        this.statusMsg = ''
        this.headerMsg = ''
        this.member = {}
        //this.prevBarcode = ''.concat(this.barcode)

        let response = await this.getMemberByBarcode({
          params: { barcode: this.barcode },
        })
        if (response.docs.length == 0) {
          this.headerMsg = 'You are not a C4 member! Please Register'
        } else {
          this.member = response.docs[0]
          let currentdate = new Date()
          let nextDay = new Date(this.member.next_payment_date)
          if (currentdate > nextDay) {
            this.isDayPassed = true
          } else {
            this.isDayPassed = false
          }
        }
      } else {
        this.isLoading = false
        return
      }

      debugger
      let q = {
        selector: {
          type: 'attendance',
          member_id: this.member._id,
          member_barcode: this.member.barcode,
          checked_in_date: this.formattedDate(),
        },
        limit: 10000000,
      }

      let response = await this.checkForAttendance(q)
      if (response.docs.length == 0) {
        let newpayload = {
          _id: 'attendance_' + uuidv4(),
          type: 'attendance',
          member_id: this.member._id,
          member_name: `${this.member.first_name} ${this.member.last_name}`,
          member_barcode: this.member.barcode,
          checked_in_date: this.formattedDate(),
          checked_in_time: this.formattedTime(),
          checked_out_time: '',
        }

        await this.markAttendance({ attendanceDetails: newpayload })
          .then(resp => {
            if (resp.ok) {
              this.statusMsg = 'Checked In Successfully!'
            }
          })
          .catch(err => {
            console.log(err)
          })
      } else {
        if (await this.isTimePassed(response.docs[0].checked_in_time)) {
          //update checkout
          if (response.docs[0].checked_out_time == '') {
            response.docs[0].checked_out_time = this.formattedTime()
            let resp = await this.markAttendance({
              attendanceDetails: response.docs[0],
            })
            if (resp.ok) {
              this.statusMsg = 'Checked Out Successfully!'
            }
          } else {
            this.statusMsg = 'You are already checked Out!'
          }
        } else {
          this.statusMsg = 'You are already checked in!'
        }
      }
      this.isLoading = false
      this.swapCards()
    },
    isTimePassed(formattedTime) {
      const currentTime = new Date()

      const [inputHours, inputMinutes, inputSeconds] = formattedTime
        .split(':')
        .map(Number)

      const inputTime = new Date()
      inputTime.setHours(inputHours, inputMinutes, inputSeconds, 0)

      const tenMinutesInMilliseconds = 10 * 60 * 1000

      return currentTime - inputTime > tenMinutesInMilliseconds
    },
    formattedDate() {
      const date = new Date()
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0') // Months are zero-based
      const day = String(date.getDate()).padStart(2, '0')

      return `${year}-${month}-${day}`
    },
    formattedTime() {
      const date = new Date()
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')

      return `${hours}:${minutes}:${seconds}`
    },
  },
}
</script>
<style scoped>
/* Container Styling */
.attendance-container {
  padding-top: 2rem;
  padding-bottom: 2rem;
}

/* Modern Card Styling */
.card1,
.card2 {
  position: relative;
  width: 100%;
  border: none;
  border-radius: 24px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  background: linear-gradient(135deg, #ecc22e 0%, #f1db79 100%);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  margin-bottom: 1rem;
}

.card1 {
  top: 0;
  z-index: 2;
}

.card2 {
  top: 0;
  z-index: 1;
}

.card2.move-slightly-down {
  transform: translateY(60px);
}

/* Payment Overdue Popup */
.payment-overdue-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  animation: fadeIn 0.3s ease-out;
}

.payment-overdue-popup {
  background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
  border-radius: 24px;
  padding: 3rem 2.5rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  max-width: 600px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  animation: shake 0.5s ease-in-out, popupSlideIn 0.4s ease-out;
}

.fail-icon-wrapper {
  width: 150px;
  height: 150px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  animation: pulseScale 2s ease-in-out infinite;
}

.fail-icon {
  font-size: 6rem;
  color: #ffffff;
}

.fail-title {
  font-size: 3rem;
  font-weight: 800;
  color: #ffffff;
  margin: 0;
  letter-spacing: -1px;
  text-transform: uppercase;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
}

.fail-subtitle {
  font-size: 2rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
}

.fail-message {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 2rem;
  margin: 1rem 0;
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.fail-message p {
  font-size: 1.5rem;
  color: #ffffff;
  margin: 0.5rem 0;
  font-weight: 500;
}

.fail-date {
  font-size: 1.75rem !important;
  font-weight: 700 !important;
  margin-top: 1rem !important;
}

.fail-action {
  background: rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(10px);
  border-radius: 50px;
  padding: 1.5rem 2.5rem;
  border: 2px solid rgba(255, 255, 255, 0.4);
}

.fail-action p {
  font-size: 1.3rem;
  color: #ffffff;
  margin: 0;
  font-weight: 600;
}

.auto-close-timer {
  margin-top: 1rem;
  padding: 0.75rem 1.5rem;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 50px;
}

.auto-close-timer p {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  font-weight: 500;
}

/* Card 1 Content Styling */
.card1-content {
  padding: 3rem 2.5rem;
}

.greeting-section {
  margin-bottom: 2rem;
  animation: fadeInUp 0.6s ease-out;
}

.greeting-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 0.5rem;
  letter-spacing: -0.5px;
}

.greeting-subtitle {
  font-size: 1.75rem;
  font-weight: 500;
  color: #34495e;
  margin: 0;
}

.info-section {
  margin: 2rem 0;
}

.info-card {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 1.5rem 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.info-label {
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #7f8c8d;
  margin-bottom: 0.5rem;
}

.info-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2c3e50;
}

.status-section {
  margin: 1.5rem 0;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: rgba(46, 204, 113, 0.15);
  backdrop-filter: blur(10px);
  color: #27ae60;
  padding: 1rem 1.75rem;
  border-radius: 50px;
  font-size: 1.25rem;
  font-weight: 600;
  border: 2px solid rgba(46, 204, 113, 0.3);
}

.status-badge i {
  font-size: 1.5rem;
}

.alert-section {
  margin-top: 1.5rem;
}

.alert-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: rgba(231, 76, 60, 0.15);
  backdrop-filter: blur(10px);
  color: #c0392b;
  padding: 1rem 1.75rem;
  border-radius: 50px;
  font-size: 1.25rem;
  font-weight: 700;
  border: 2px solid rgba(231, 76, 60, 0.3);
  animation: pulse 2s ease-in-out infinite;
}

.alert-badge i {
  font-size: 1.5rem;
}

@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  10%,
  30%,
  50%,
  70%,
  90% {
    transform: translateX(-5px);
  }
  20%,
  40%,
  60%,
  80% {
    transform: translateX(5px);
  }
}

@keyframes pulseScale {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

/* Card 2 Scan Container Styling */
.scan-container {
  padding: 4rem 2rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
}

.scan-icon-wrapper {
  width: 120px;
  height: 120px;
  background: rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(10px);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  animation: float 3s ease-in-out infinite;
}

.scan-icon {
  font-size: 4rem;
  color: #2c3e50;
}

.scan-title {
  font-size: 2rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
  letter-spacing: -0.5px;
}

.input-wrapper {
  width: 100%;
  max-width: 500px;
  position: relative;
}

.barcode-input {
  width: 100%;
  height: 70px;
  border: 3px solid rgba(255, 255, 255, 0.5);
  border-radius: 16px;
  font-size: 1.5rem;
  font-weight: 600;
  text-align: center;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  color: #2c3e50;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.barcode-input:focus {
  outline: none;
  border-color: #2c3e50;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  transform: scale(1.02);
}

.barcode-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.barcode-input::placeholder {
  color: #95a5a6;
  font-weight: 500;
}

/* Remove number input arrows */
.barcode-input::-webkit-outer-spin-button,
.barcode-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.barcode-input[type='number'] {
  -moz-appearance: textfield;
}

.loading-indicator {
  position: absolute;
  top: 50%;
  right: 1.5rem;
  transform: translateY(-50%);
}

.spinner-small {
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top: 3px solid #ecc22e;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
}

/* Loading Screen */
#loading-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.spinner {
  border: 10px solid rgba(255, 255, 255, 0.2);
  border-top: 10px solid #ecc22e;
  border-radius: 50%;
  width: 100px;
  height: 100px;
  animation: spin 1.5s linear infinite;
}

/* Animations */
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes popupSlideIn {
  from {
    transform: translateY(-50px) scale(0.9);
    opacity: 0;
  }
  to {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .card1-content {
    padding: 2rem 1.5rem;
  }

  .payment-overdue-popup {
    padding: 2rem 1.5rem;
    width: 95%;
  }

  .fail-icon-wrapper {
    width: 120px;
    height: 120px;
  }

  .fail-icon {
    font-size: 4.5rem;
  }

  .fail-title {
    font-size: 2.2rem;
  }

  .fail-subtitle {
    font-size: 1.6rem;
  }

  .fail-message p {
    font-size: 1.2rem;
  }

  .fail-date {
    font-size: 1.4rem !important;
  }

  .fail-action p {
    font-size: 1.1rem;
  }

  .greeting-title {
    font-size: 2rem;
  }

  .greeting-subtitle {
    font-size: 1.5rem;
  }

  .scan-container {
    padding: 3rem 1.5rem;
  }

  .scan-icon-wrapper {
    width: 100px;
    height: 100px;
  }

  .scan-icon {
    font-size: 3rem;
  }

  .scan-title {
    font-size: 1.5rem;
  }

  .barcode-input {
    height: 60px;
    font-size: 1.25rem;
  }
}
</style>

<style >
.app-body .main {
  background-color: #111213 !important;
}
</style>
